import { motion } from "framer-motion";
import {
    Rocket,
    Heart,
    Zap,
    Coffee,
    Globe2,
    Smile,
    ArrowRight,
    Search,
    MapPin,
    Briefcase
} from "lucide-react";
import SEO from "../components/SEO";

export default function Careers() {
    const openings = [
        {
            title: "Senior AI Engineer",
            department: "Engineering",
            location: "Remote / New Delhi",
            type: "Full-Time",
            desc: "Help us build the next generation of business intelligence models."
        },
        {
            title: "Product Designer",
            department: "Design",
            location: "Remote",
            type: "Full-Time",
            desc: "Create beautiful, intuitive interfaces for the world's best entrepreneurs."
        },
        {
            title: "Growth Marketer",
            department: "Marketing",
            location: "Remote",
            type: "Full-Time",
            desc: "Scale our mission to millions of small businesses worldwide."
        },
        {
            title: "Customer Success Lead",
            department: "Support",
            location: "New Delhi",
            type: "Full-Time",
            desc: "Ensure our users are getting the absolute most out of the blinqbooks suite."
        }
    ];

    const perks = [
        { icon: <Globe2 className="w-6 h-6 text-blue-500" />, title: "Remote First", desc: "Work from anywhere in the world. We value output over seat time." },
        { icon: <Zap className="w-6 h-6 text-amber-500" />, title: "Ownership", desc: "Generous equity packages for every full-time team member." },
        { icon: <Heart className="w-6 h-6 text-rose-500" />, title: "Wellness", desc: "Comprehensive health insurance and annual wellness stipends." },
        { icon: <Coffee className="w-6 h-6 text-emerald-500" />, title: "Learning", desc: "Infinite budget for books, courses, and professional growth." }
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
            <SEO
                title="Careers - Join the Mission"
                description="Help us build the future of AI-driven business. Explore open roles at blinqbooks and join a remote-first, high-growth team."
            />

            {/* Hero Section */}
            <section className="relative py-24 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase mb-8"
                    >
                        <Rocket className="w-4 h-4" />
                        We're Hiring
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-7xl font-bold mb-8 text-primary dark:text-white leading-tight"
                    >
                        Build the Future of <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-500">Autonomous Business</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-xl text-secondary dark:text-gray-300 max-w-3xl mx-auto leading-relaxed"
                    >
                        blinqbooks is more than a tool—it's a mission to empower 100 million entrepreneurs with AI. We're looking for builders, dreamers, and doers.
                    </motion.p>
                </div>

                {/* Animated background shapes */}
                <div className="absolute top-1/4 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
            </section>

            {/* Perks & Benefits */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {perks.map((perk, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="p-8 rounded-2xl bg-slate-500/[0.02] dark:bg-white/[0.01] hover:bg-blue-500/[0.06] transition-all duration-300"
                            >
                                <div className="mb-6">{perk.icon}</div>
                                <h3 className="text-lg font-bold mb-3 text-primary dark:text-white">{perk.title}</h3>
                                <p className="text-sm text-secondary dark:text-gray-400 leading-relaxed">{perk.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Open Roles */}
            <section className="py-24 px-4">
                <div className="max-w-5xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
                        <div>
                            <h2 className="text-3xl md:text-5xl font-bold text-primary dark:text-white mb-4">Open Roles</h2>
                            <p className="text-secondary dark:text-gray-400">Join our growing team and make an impact from day one.</p>
                        </div>
                        <div className="relative w-full md:w-auto">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Filter by role..."
                                className="w-full md:w-80 pl-11 pr-4 py-3 rounded-xl bg-slate-500/5 dark:bg-white/5 border border-slate-200 dark:border-slate-800 text-sm focus:ring-2 focus:ring-accent outline-none"
                            />
                        </div>
                    </div>

                    <div className="space-y-4">
                        {openings.map((job, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="group p-6 md:p-8 rounded-2xl bg-slate-500/[0.02] dark:bg-white/[0.01] hover:bg-slate-500/[0.04] dark:hover:bg-white/[0.02] transition-all cursor-pointer"
                            >
                                <div className="flex flex-col md:flex-row md:items-center gap-6">
                                    <div className="flex-grow">
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <span className="px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[10px] font-bold uppercase tracking-wider">{job.department}</span>
                                            <span className="px-3 py-1 rounded-full bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                                                <MapPin className="w-3 h-3" /> {job.location}
                                            </span>
                                        </div>
                                        <h3 className="text-xl font-bold text-primary dark:text-white group-hover:text-accent transition-colors mb-2">{job.title}</h3>
                                        <p className="text-sm text-secondary dark:text-gray-400">{job.desc}</p>
                                    </div>
                                    <div className="flex items-center gap-4 shrink-0">
                                        <div className="hidden md:block text-right">
                                            <div className="text-sm font-bold dark:text-white uppercase tracking-tighter">{job.type}</div>
                                            <div className="text-[10px] text-gray-400">Posted 2 days ago</div>
                                        </div>
                                        <div className="p-3 rounded-full bg-slate-500/10 dark:bg-white/5 group-hover:bg-accent group-hover:text-white transition-all">
                                            <ArrowRight className="w-5 h-5" />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <p className="text-secondary dark:text-gray-400 mb-6 font-medium">Don't see a role that fits?</p>
                        <button className="btn-primary flex items-center gap-2 mx-auto">
                            Send General Application <Briefcase className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* Cultare CTA */}
            <section className="py-24 bg-primary dark:bg-slate-950 text-white overflow-hidden relative">
                <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
                    <Smile className="w-16 h-16 text-accent mx-auto mb-8 opacity-40 animate-bounce" />
                    <h2 className="text-4xl md:text-6xl font-black mb-8 leading-tight italic">Culture for Creators.</h2>
                    <p className="text-xl text-blue-100/80 mb-12 max-w-2xl mx-auto">
                        We don't just build apps. We build careers. Join a team where your work actually matters and your voice is always heard.
                    </p>
                    <div className="grid grid-cols-3 gap-8 text-center max-w-lg mx-auto mb-12">
                        <div>
                            <div className="text-2xl font-bold">100%</div>
                            <div className="text-[10px] uppercase text-blue-300 tracking-widest">Remote</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold">Unlimited</div>
                            <div className="text-[10px] uppercase text-blue-300 tracking-widest">PTO</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold">4.9/5</div>
                            <div className="text-[10px] uppercase text-blue-300 tracking-widest">Glassdoor</div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
